import { getAppMode } from '@/shared/utils';
import type { AnalyticsSettings } from '@/shared/types';
import type { AnalyticsEvent, EventParams, ScreenName } from './events';

/** Растёт при несовместимых изменениях каталога событий */
const SCHEMA_VERSION = 1;

export interface AnalyticsContext {
  auth_state: 'guest' | 'authorized';
  region_code?: string;
}

type DimensionValues = Record<string, string | number | boolean | undefined>;

/**
 * Флаг «следующая отправка открывает новый визит» переживает закрытие
 * приложения: MAX закрывает мини-приложение сразу после выхода, и без флага
 * Matomo склеил бы следующий запуск с визитом вышедшего человека
 */
const NEW_VISIT_KEY = 'analytics_new_visit';

function storeNewVisitFlag(value: boolean): void {
  try {
    if (value) localStorage.setItem(NEW_VISIT_KEY, '1');
    else localStorage.removeItem(NEW_VISIT_KEY);
  } catch {
    // без localStorage новый визит откроется только в текущем запуске
  }
}

function readNewVisitFlag(): boolean {
  try {
    return localStorage.getItem(NEW_VISIT_KEY) === '1';
  } catch {
    return false;
  }
}

let config: AnalyticsSettings | undefined;
let disabled = false;
let pending: Array<() => void> = [];
let context: AnalyticsContext = { auth_state: 'guest' };
let userId: string | undefined;
let lastScreenUrl: string | undefined;
let currentScreen: ScreenName | undefined;
let startNewVisit = false;

function push(...command: unknown[]): void {
  window._paq = window._paq ?? [];
  window._paq.push(command);
}

function debugLog(...args: unknown[]): void {
  if (window.__CONFIG__?.analyticsDebug) console.info('[analytics]', ...args);
}

/** Сбой аналитики не должен ломать сценарий, из которого её вызвали */
function run(fn: () => void): void {
  try {
    fn();
  } catch (err) {
    console.error('[analytics]', err);
  }
}

const MAX_PENDING = 100;

/**
 * Настройки приходят из /v5/settings уже после старта. До них отправки
 * копятся вместе с контекстом на момент вызова и уходят по порядку
 */
function whenReady(fn: () => void): void {
  if (disabled) return;
  if (config) {
    run(fn);
    return;
  }

  const snapshot = context;
  if (pending.length >= MAX_PENDING) pending.shift();
  pending.push(() => {
    const current = context;
    context = snapshot;
    try {
      fn();
    } finally {
      context = current;
    }
  });
}

function appContext(): string {
  if (getAppMode() === 'max') return 'max_miniapp';

  const standalone =
    window.matchMedia?.('(display-mode: standalone)').matches ||
    (navigator as { standalone?: boolean }).standalone === true;

  return standalone ? 'pwa_installed' : 'browser';
}

/** Пишет значения в измерения и возвращает id тех, что выставлены */
function setDimensions(values: DimensionValues): number[] {
  const ids: number[] = [];

  for (const [name, value] of Object.entries(values)) {
    const id = config?.dimensions[name];
    if (!id || value === undefined || value === '') continue;

    push('setCustomDimension', id, String(value));
    ids.push(id);
  }

  return ids;
}

/**
 * Измерения уровня действия живут только на время одной отправки: Matomo
 * держит выставленное значение до сброса, и без него оно перетекло бы
 * в следующее событие
 */
function sendWithActionDimensions(
  values: DimensionValues,
  send: () => void
): void {
  const ids = setDimensions({
    auth_state: context.auth_state,
    region_code: context.region_code,
    event_origin: 'client',
    analytics_schema_version: SCHEMA_VERSION,
    ...values,
  });

  const newVisit = startNewVisit;
  if (newVisit) push('appendToTrackingUrl', 'new_visit=1');

  send();

  if (newVisit) {
    push('appendToTrackingUrl', '');
    startNewVisit = false;
    storeNewVisitFlag(false);
  }

  ids.forEach((id) => push('deleteCustomDimension', id));
}

function loadScript(src: string): void {
  const script = document.createElement('script');
  script.async = true;
  script.src = src;
  // Блокировщик или недоступный Matomo: очередь копится и никуда не уходит,
  // приложение работает как обычно
  script.onerror = () => debugLog('matomo.js не загрузился', src);
  document.head.appendChild(script);
}

export function initAnalytics(initialContext: AnalyticsContext): void {
  context = initialContext;
  startNewVisit = readNewVisitFlag();
}

/**
 * Настройки из /v5/settings. Без блока analytics аналитика выключена до
 * перезапуска: накопленное отбрасывается, matomo.js не загружается
 */
export function configureAnalytics(settings?: AnalyticsSettings): void {
  if (config || disabled) return;

  if (!settings) {
    disabled = true;
    pending = [];
    debugLog('выключена: в настройках нет analytics');
    return;
  }

  try {
    const url = new URL(settings.url, window.location.origin).href;
    config = { ...settings, url };

    push('setTrackerUrl', `${url}matomo.php`);
    push('setSiteId', String(config.siteId));
    setDimensions({
      platform: 'web',
      app_context: appContext(),
      app_version: window.__CONFIG__?.version,
    });

    loadScript(`${url}matomo.js`);
    debugLog('включена', config);
  } catch (err) {
    config = undefined;
    disabled = true;
    pending = [];
    console.error('[analytics]', err);
    return;
  }

  const queued = pending;
  pending = [];
  queued.forEach(run);
}

export function setAnalyticsContext(next: Partial<AnalyticsContext>): void {
  context = { ...context, ...next };
}

export function track(event: AnalyticsEvent, params: EventParams = {}): void {
  whenReady(() => {
    const { label, value, ...dimensions } = params;
    const category = event.slice(0, event.indexOf('_'));
    const name =
      label ??
      dimensions.error_type ??
      dimensions.method ??
      dimensions.sort ??
      dimensions.filter_name ??
      dimensions.source;

    sendWithActionDimensions(dimensions, () =>
      push('trackEvent', category, event, name, value)
    );
    debugLog('event', event, params);
  });
}

/**
 * Просмотр экрана. path — нормализованный путь без id и строки запроса;
 * хост всегда канонический, иначе отчёт по страницам распадётся
 * по региональным поддоменам MAX
 */
export function trackScreen(screen: ScreenName, path: string): void {
  if (disabled) return;

  const origin = window.__CONFIG__?.pwaOrigin || window.location.origin;
  const url = `${origin}${path}`;
  const referrer = lastScreenUrl;
  lastScreenUrl = url;
  currentScreen = screen;

  whenReady(() => {
    push('setCustomUrl', url);
    if (referrer) push('setReferrerUrl', referrer);
    push('setDocumentTitle', screen);
    sendWithActionDimensions({}, () => push('trackPageView'));
    debugLog('screen', screen, path);
  });
}

/** После входа: визит продолжается, дальше действия привязаны к человеку */
export function setAnalyticsUser(analyticsId: string | undefined): void {
  if (disabled || !analyticsId || analyticsId === userId) return;

  userId = analyticsId;
  whenReady(() => {
    push('setUserId', analyticsId);
    debugLog('user', analyticsId);
  });
}

/** Последний засчитанный экран — для source действий вне конкретного экрана */
export function getCurrentScreen(): ScreenName | undefined {
  return currentScreen;
}

export function hasAnalyticsUser(): boolean {
  return Boolean(userId);
}

function randomVisitorId(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(8));
  return Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
}

/**
 * После выхода — новый посетитель и новый визит, иначе следующий человек
 * на устройстве склеился бы с предыдущим. resetUserId в matomo.js только
 * снимает User ID, visitor id из cookie остаётся прежним — его меняем сами
 */
export function resetAnalyticsUser(): void {
  if (disabled) return;

  userId = undefined;
  startNewVisit = true;
  storeNewVisitFlag(true);
  // Matomo держит referrer до замены: новый визит не должен начинаться
  // с экрана предыдущего человека
  lastScreenUrl = undefined;

  whenReady(() => {
    push('resetUserId');
    push('deleteCookies');
    push('setVisitorId', randomVisitorId());
    push('setReferrerUrl', '');
    debugLog('user reset');
  });
}
